// pages/Users/ListUsers.js
import React, { useState, useEffect } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import DataTable from "react-data-table-component";
import {
  Box,
  Typography,
  Button,
  Alert,
  Modal,
  CircularProgress,
  Chip,
  useTheme,
} from "@mui/material";
import EditUser from "./EditUser";

export default function ListUsers({ refresh, onRefresh }) {
  const theme = useTheme();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, [refresh]);


  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/api/users`);
      if (response.data.success) {
        const data =
          typeof response.data.data === "string"
            ? JSON.parse(response.data.data)
            : response.data.data;
        setUsers(data.users || []);
        setError(null);
      } else {
        setError(response.data.message || "Failed to fetch users");
      }
    } catch (err) {
      setError("Error fetching users: " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm("Are you sure you want to remove this user?")) return;

    try {
      const response = await axiosInstance.delete(`/api/users/${userId}`);
      if (response.data.success) {
        setUsers(users.filter((u) => u.user_id !== userId));
        setSuccess("User removed successfully");
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(response.data.message || "Failed to remove user");
      }
    } catch (err) {
      setError("Error removing user: " + (err.response?.data?.message || err.message));
    }
  };

  const handleEdit = (user) => {
    setCurrentUser(user);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentUser(null);
  };

  const updateModal = () => {
    fetchUsers();
    closeModal();
    if (onRefresh) onRefresh();
  };

  // ✅ Custom MUI-based DataTable styling for dark mode
const customDataTableStyles = {
  table: {
    style: {
      backgroundColor: theme.palette.background.paper,
    },
  },
  headRow: {
    style: {
      backgroundColor:
        theme.palette.mode === "dark" ? "#1e293b" : "#f1f5f9",
      color: theme.palette.text.primary,
      borderBottom: `1px solid ${
        theme.palette.mode === "dark" ? "#334155" : "#cbd5e1"
      }`,
    },
  },
  headCells: {
    style: {
      fontWeight: 600,
      fontSize: "14px",
      color: theme.palette.text.primary,
    },
  },
  rows: {
    style: {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
      transition: "background-color 0.2s ease",
      "&:hover": {
        backgroundColor:
          theme.palette.mode === "dark" ? "#131b2b !important" : "#f8fafc",
      },
      "&.rdt_Row:hover": {
        backgroundColor:
          theme.palette.mode === "dark" ? "#131b2b !important" : "#f8fafc",
      },
      "&.rdt_Row--selected": {
        backgroundColor:
          theme.palette.mode === "dark" ? "#131b2b !important" : "#e2e8f0",
      },
    },
  },
  pagination: {
    style: {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
      borderTop: `1px solid ${
        theme.palette.mode === "dark" ? "#334155" : "#e2e8f0"
      }`,
    },
  },
  expanderRow: {
    style: {
      backgroundColor: theme.palette.mode === "dark" ? "#0f172a" : "#f9fafb",
    },
  },
};

  const columns = [
    {
      name: "Name",
      width: '120px', 
      selector: (row) =>
        row.profile
          ? `${row.profile.fname || ""} ${row.profile.lname || ""}`.trim()
          : "N/A",
      sortable: true,
    },
    { name: "Email", selector: (row) => row.user_email, sortable: true },
    {
      name: "Role",
      width: '280px',
      cell: (row) => {
        const roleColors = {
          admin: { bg: '#f3e8ff', color: '#7c3aed', label: 'Admin' },
          employee: { bg: '#dbeafe', color: '#2563eb', label: 'Employee' },
          customer: { bg: '#d1fae5', color: '#059669', label: 'Customer' },
          student: { bg: '#fef3c7', color: '#d97706', label: 'Student' },
        };
        const roleKey = row.role_in_team?.toLowerCase();
        const roleStyle = roleColors[roleKey] || { bg: '#f1f5f9', color: '#475569', label: row.role_in_team };
        const panelAccess = ['admin', 'employee'].includes(roleKey) ? 'Both' : 'Customer';
        return (
          <Box display="flex" gap={0.5} alignItems="center" flexWrap="wrap">
            <Chip
              label={roleStyle.label}
              size="small"
              sx={{ bgcolor: roleStyle.bg, color: roleStyle.color, fontWeight: 600 }}
            />
            <Chip
              label={row.is_system_role_in_team ? "Global" : "Custom"}
              variant="outlined"
              color={row.is_system_role_in_team ? "success" : "info"}
              size="small"
            />
            <Chip
              label={panelAccess === 'Both' ? 'Both Panels' : 'Customer Panel'}
              variant="outlined"
              size="small"
              sx={{
                borderColor: panelAccess === 'Both' ? '#8b5cf6' : '#10b981',
                color: panelAccess === 'Both' ? '#8b5cf6' : '#10b981',
                fontSize: '0.7rem',
              }}
            />
          </Box>
        );
      },
    },
    {
      name: "Status",
      width: '150px', 
      cell: (row) => (
        <Chip
          label={row.verified ? "Verified" : "Pending"}
          color={row.verified ? "success" : "warning"}
          size="small"
        />
      ),
      sortable: true,
    },
    {
      name: "Joined",
      width: '150px', 
      selector: (row) =>
        row.created_on ? new Date(row.created_on).toLocaleDateString() : "N/A",
      sortable: true,
    },
    {
      name: "Action",
      cell: (row) => (
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => handleEdit(row)}
          >
            <i className="fa fa-pencil" />
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={() => handleDelete(row.user_id)}
          >
            <i className="fa fa-trash-o" />
          </Button>
        </Box>
      ),
      ignoreRowClick: true,
    },
  ];

  const muiModalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: { xs: "90%", sm: "80%", md: "600px" },
    bgcolor: "background.paper",
    boxShadow: 24,
    borderRadius: 2,
    outline: "none",
    p: 0,
  };

  const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3, pb: 2, borderBottom: '1px solid rgba(0,0,0,0.06)' }}
      >
        <Typography sx={{ fontFamily, fontSize: '1.1rem', fontWeight: 600, color: '#1e293b' }}>
          Team Members
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            fetchUsers();
          }}
          disabled={loading}
          sx={{
            fontFamily,
            textTransform: 'none',
            borderRadius: '10px',
            px: 2,
            py: 0.75,
            borderColor: 'rgba(0,0,0,0.15)',
            color: '#475569',
            '&:hover': { borderColor: '#8b5cf6', color: '#8b5cf6' },
          }}
        >
          Refresh
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <i className="fa fa-info-circle" />{" "}
        <strong>Roles:</strong>{" "}
        <Chip label="Admin" size="small" sx={{ bgcolor: '#f3e8ff', color: '#7c3aed', fontWeight: 600, mx: 0.25 }} /> &{" "}
        <Chip label="Employee" size="small" sx={{ bgcolor: '#dbeafe', color: '#2563eb', fontWeight: 600, mx: 0.25 }} /> access both panels |{" "}
        <Chip label="Customer" size="small" sx={{ bgcolor: '#d1fae5', color: '#059669', fontWeight: 600, mx: 0.25 }} /> &{" "}
        <Chip label="Student" size="small" sx={{ bgcolor: '#fef3c7', color: '#d97706', fontWeight: 600, mx: 0.25 }} /> Customer Panel only
      </Alert>

      {loading ? (
        <Box display="flex" flexDirection="column" alignItems="center" py={4}>
          <CircularProgress size={30} />
          <Typography mt={2}>Loading users...</Typography>
        </Box>
      ) : users.length === 0 ? (
        <Box textAlign="center" py={4}>
          <i className="fa fa-users fa-3x text-muted" />
          <Typography mt={2} color="text.primary">
            No team members found
          </Typography>
        </Box>
      ) : (
        <DataTable
          columns={columns}
          data={users}
          pagination
          responsive
          highlightOnHover
          pointerOnHover
          theme={theme.palette.mode === "dark" ? "dark" : "default"}
          customStyles={customDataTableStyles}
          expandableRows
          expandableRowsComponent={({ data }) => (
            <Box p={2}>
              <Typography variant="body2" color="text.primary">
                <strong>Email:</strong> {data.user_email}
                <br />
                <strong>Joined:</strong>{" "}
                {data.created_on
                  ? new Date(data.created_on).toLocaleString()
                  : "N/A"}
              </Typography>
            </Box>
          )}
        />
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 3 }}>
          <i className="fa fa-exclamation-triangle" /> {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mt: 3 }}>
          <i className="fa fa-check" /> {success}
        </Alert>
      )}

      <Modal open={isModalOpen} onClose={closeModal} aria-labelledby="edit-user-modal">
        <Box sx={muiModalStyle}>
          <EditUser user={currentUser} onClose={closeModal} onUpdate={updateModal} />
        </Box>
      </Modal>
    </Box>
  );
}
